import { useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Checkbox,
  Drawer,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconBan,
  IconCheck,
  IconPlus,
  IconRotateClockwise,
  IconSearch,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatMoney } from '@erp/shared';
import { ApiError } from '../api/client';
import { unitsOfMeasureApi, vatRatesApi } from '../api/catalogs';
import {
  NOMENCLATURE_KIND_LABELS,
  nomenclatureApi,
  nomenclatureGroupsApi,
  type Nomenclature,
  type NomenclatureInput,
  type NomenclatureKind,
} from '../api/nomenclature';
import { buildGroupOptions, NomenclatureGroupTree } from '../components/NomenclatureGroupTree';
import { RegistryPage } from '../components/RegistryPage';
import { useAuth } from '../auth/AuthContext';

const KIND_OPTIONS = Object.entries(NOMENCLATURE_KIND_LABELS).map(([value, label]) => ({ value, label }));

const emptyForm: NomenclatureInput = {
  article: '',
  name: '',
  fullName: '',
  kind: 'GOODS',
  unitId: '',
  vatRateId: '',
  price: '',
  tnvedCode: '',
  countryCode: '',
  isTraceable: false,
  groupId: '',
};

export function NomenclaturePage() {
  const { hasRole } = useAuth();
  const canEdit = hasRole('ADMIN', 'ACCOUNTANT', 'MANAGER');
  const queryClient = useQueryClient();

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<NomenclatureInput>(emptyForm);
  const [formError, setFormError] = useState<string[] | null>(null);

  const groups = useQuery({
    queryKey: ['nomenclature-groups'],
    queryFn: () => nomenclatureGroupsApi.list(),
  });
  const units = useQuery({ queryKey: ['units-of-measure'], queryFn: () => unitsOfMeasureApi.list() });
  const vatRates = useQuery({ queryKey: ['vat-rates'], queryFn: () => vatRatesApi.list() });

  const list = useQuery({
    queryKey: ['nomenclature', { search: debouncedSearch, groupId: selectedGroupId, includeInactive }],
    queryFn: () =>
      nomenclatureApi.list({
        search: debouncedSearch,
        groupId: selectedGroupId ?? undefined,
        includeInactive,
      }),
  });

  const unitOptions = (units.data?.items ?? []).map((u) => ({ value: u.id, label: `${u.symbol} — ${u.name}` }));
  const vatRateOptions = (vatRates.data?.items ?? []).map((r) => ({ value: r.id, label: r.name }));
  const groupOptions = [{ value: '', label: 'Без группы' }, ...buildGroupOptions(groups.data?.items ?? [])];

  function openCreate() {
    setEditingId(null);
    setForm({ ...emptyForm, groupId: selectedGroupId ?? '' });
    setFormError(null);
    setDrawerOpen(true);
  }

  function openEdit(item: Nomenclature) {
    setEditingId(item.id);
    setForm({
      article: item.article ?? '',
      name: item.name,
      fullName: item.fullName ?? '',
      kind: item.kind,
      unitId: item.unitId,
      vatRateId: item.vatRateId ?? '',
      price: item.price ?? '',
      tnvedCode: item.tnvedCode ?? '',
      countryCode: item.countryCode ?? '',
      isTraceable: item.isTraceable,
      groupId: item.groupId ?? '',
    });
    setFormError(null);
    setDrawerOpen(true);
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: NomenclatureInput = {
        ...form,
        article: form.article || null,
        fullName: form.fullName || null,
        vatRateId: form.vatRateId || null,
        price: form.price || null,
        tnvedCode: form.tnvedCode || null,
        countryCode: form.countryCode || null,
        groupId: form.groupId || null,
      };
      return editingId ? nomenclatureApi.update(editingId, payload) : nomenclatureApi.create(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['nomenclature'] });
      notifications.show({ color: 'green', message: 'Позиция номенклатуры сохранена' });
      setDrawerOpen(false);
    },
  });

  function handleSubmit() {
    setFormError(null);
    if (!form.name.trim()) {
      setFormError(['Укажите наименование']);
      return;
    }
    if (!form.unitId) {
      setFormError(['Укажите единицу измерения']);
      return;
    }
    saveMutation.mutate(undefined, {
      onError: (err) => {
        setFormError(err instanceof ApiError ? (err.details ?? [err.message]) : ['Не удалось сохранить']);
      },
    });
  }

  const toggleActiveMutation = useMutation({
    mutationFn: (item: Nomenclature) =>
      item.isActive ? nomenclatureApi.deactivate(item.id) : nomenclatureApi.activate(item.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['nomenclature'] });
    },
  });

  const createGroup = useMutation({
    mutationFn: nomenclatureGroupsApi.create,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['nomenclature-groups'] }),
  });
  const renameGroup = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => nomenclatureGroupsApi.update(id, { name }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['nomenclature-groups'] }),
  });
  const deleteGroup = useMutation({
    mutationFn: (id: string) => nomenclatureGroupsApi.remove(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['nomenclature-groups'] }),
  });

  const items = list.data?.items ?? [];

  return (
    <RegistryPage
      title="Номенклатура"
      action={
        canEdit && (
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            Создать позицию
          </Button>
        )
      }
      filters={
        <Group>
          <TextInput
            placeholder="Поиск по наименованию или артикулу"
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={320}
          />
          <Checkbox
            label="Показывать отключённые"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.currentTarget.checked)}
          />
        </Group>
      }
      /* Дерево групп — часть навигации по разделу, а не строка списка:
         прокручивается независимо от таблицы позиций. */
      aside={
        <NomenclatureGroupTree
          groups={groups.data?.items ?? []}
          selectedId={selectedGroupId}
          onSelect={setSelectedGroupId}
          canEdit={canEdit}
          onCreate={(data) => createGroup.mutateAsync(data).then(() => undefined)}
          onRename={(id, name) => renameGroup.mutateAsync({ id, name }).then(() => undefined)}
          onDelete={(id) => deleteGroup.mutateAsync(id).then(() => undefined)}
        />
      }
    >
      <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Наименование</Table.Th>
                <Table.Th>Артикул</Table.Th>
                <Table.Th>Вид</Table.Th>
                <Table.Th>Ед. изм.</Table.Th>
                <Table.Th>Ставка НДС</Table.Th>
                <Table.Th>Цена</Table.Th>
                <Table.Th>Статус</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((item) => (
                <Table.Tr key={item.id} style={{ cursor: 'pointer' }} onClick={() => openEdit(item)}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {item.name}
                    </Text>
                  </Table.Td>
                  <Table.Td>{item.article ?? '—'}</Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {NOMENCLATURE_KIND_LABELS[item.kind]}
                    </Text>
                  </Table.Td>
                  <Table.Td>{item.unit.symbol}</Table.Td>
                  <Table.Td>{item.vatRate?.name ?? '—'}</Table.Td>
                  <Table.Td>{item.price ? formatMoney(item.price) : '—'}</Table.Td>
                  <Table.Td>
                    <Badge color={item.isActive ? 'green' : 'gray'} variant="light">
                      {item.isActive ? 'Активна' : 'Отключена'}
                    </Badge>
                  </Table.Td>
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    {canEdit && (
                      <Tooltip label={item.isActive ? 'Отключить' : 'Включить'}>
                        <ActionIcon
                          variant="subtle"
                          color={item.isActive ? 'red' : 'green'}
                          onClick={() => toggleActiveMutation.mutate(item)}
                        >
                          {item.isActive ? <IconBan size={16} /> : <IconRotateClockwise size={16} />}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

      {!list.isLoading && items.length === 0 && (
        <Text c="dimmed" size="sm" ta="center" py="xl">
          Позиции не найдены
        </Text>
      )}

      <Drawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editingId ? 'Позиция номенклатуры' : 'Новая позиция номенклатуры'}
        position="right"
        size="lg"
        padding="lg"
      >
        <Stack gap="sm">
          {formError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              <Stack gap={2}>
                {formError.map((e, i) => (
                  <Text key={i} size="sm">
                    {e}
                  </Text>
                ))}
              </Stack>
            </Alert>
          )}

          <Group grow>
            <TextInput
              label="Наименование"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.currentTarget.value })}
              required
              disabled={!canEdit}
            />
            <TextInput
              label="Артикул"
              value={form.article ?? ''}
              onChange={(e) => setForm({ ...form, article: e.currentTarget.value })}
              disabled={!canEdit}
            />
          </Group>

          <TextInput
            label="Полное наименование (для печатных форм)"
            value={form.fullName ?? ''}
            onChange={(e) => setForm({ ...form, fullName: e.currentTarget.value })}
            disabled={!canEdit}
          />

          <Group grow>
            <Select
              label="Вид"
              data={KIND_OPTIONS}
              value={form.kind}
              onChange={(v) => setForm((f) => ({ ...f, kind: (v as NomenclatureKind) ?? f.kind }))}
              disabled={!canEdit}
              allowDeselect={false}
            />
            <Select
              label="Группа"
              data={groupOptions}
              value={form.groupId || ''}
              onChange={(v) => setForm((f) => ({ ...f, groupId: v ?? '' }))}
              disabled={!canEdit}
              searchable
            />
          </Group>

          <Group grow>
            <Select
              label="Единица измерения"
              placeholder="Выберите единицу"
              data={unitOptions}
              value={form.unitId || null}
              onChange={(v) => setForm((f) => ({ ...f, unitId: v ?? '' }))}
              disabled={!canEdit}
              required
              searchable
            />
            <Select
              label="Ставка НДС"
              placeholder="Не указана"
              data={vatRateOptions}
              value={form.vatRateId || ''}
              onChange={(v) => setForm((f) => ({ ...f, vatRateId: v ?? '' }))}
              disabled={!canEdit}
              clearable
            />
          </Group>

          <TextInput
            label="Цена продажи без НДС"
            placeholder="0.00"
            value={form.price ?? ''}
            onChange={(e) => setForm({ ...form, price: e.currentTarget.value })}
            disabled={!canEdit}
          />

          {form.kind === 'GOODS' && (
            <Group grow>
              <TextInput
                label="Код ТН ВЭД ЕАЭС"
                value={form.tnvedCode ?? ''}
                onChange={(e) => setForm({ ...form, tnvedCode: e.currentTarget.value })}
                disabled={!canEdit}
              />
              <TextInput
                label="Код страны происхождения (ОКСМ)"
                value={form.countryCode ?? ''}
                onChange={(e) => setForm({ ...form, countryCode: e.currentTarget.value })}
                disabled={!canEdit}
              />
            </Group>
          )}

          <Checkbox
            label="Прослеживаемый товар / маркировка"
            checked={form.isTraceable ?? false}
            onChange={(e) => setForm({ ...form, isTraceable: e.currentTarget.checked })}
            disabled={!canEdit}
          />

          {canEdit && (
            <Button
              leftSection={<IconCheck size={16} />}
              onClick={handleSubmit}
              loading={saveMutation.isPending}
              mt="xs"
            >
              Сохранить
            </Button>
          )}
        </Stack>
      </Drawer>
    </RegistryPage>
  );
}
